import { Schema } from "effect";
// 从子路径 import 而非 shared barrel：barrel 含 token-cipher(node:crypto)，
// 前端经 platform-api 引入会把 node:crypto 打进浏览器 bundle 而报错。
import { RewardRules } from "@mip-tg-bot-turntable/shared/reward-rules";

export const PRIZE_TYPE_VALUES = ["thanks", "extra_spins", "physical", "virtual"] as const;
export const PrizeType = Schema.Literal(...PRIZE_TYPE_VALUES);
export type PrizeType = Schema.Schema.Type<typeof PrizeType>;

// 转盘可选颜色字段的默认值（单一来源）。schema decode 缺键时回退到此；
// Admin 回填表单时也用它垫底，避免存量 bot 缺键导致颜色选择器空白。
export const WHEEL_COLOR_DEFAULTS = {
  ring_color: "#E0B65A",
  center_button_color: "#E0B65A",
  center_button_disabled_color: "#5a4a2a",
  center_text_color: "#3a1c08",
  center_text_disabled_color: "rgba(58,28,8,0.55)",
  sector_fallback_color: "#1F1841",
  sector_text_color: null,
} as const;

export const WheelConfig = Schema.Struct({
  center_label_key: Schema.String,
  center_disabled_label_key: Schema.String,
  background_color: Schema.String,
  background_image_url: Schema.NullOr(Schema.String),
  sector_colors: Schema.Array(Schema.String).pipe(Schema.minItems(3)),
  // 外观颜色：均为可选，存量 wheel_config 缺这些键时 decode 回退默认值。
  ring_color: Schema.optionalWith(Schema.String, {
    default: () => WHEEL_COLOR_DEFAULTS.ring_color,
  }),
  center_button_color: Schema.optionalWith(Schema.String, {
    default: () => WHEEL_COLOR_DEFAULTS.center_button_color,
  }),
  center_button_disabled_color: Schema.optionalWith(Schema.String, {
    default: () => WHEEL_COLOR_DEFAULTS.center_button_disabled_color,
  }),
  center_text_color: Schema.optionalWith(Schema.String, {
    default: () => WHEEL_COLOR_DEFAULTS.center_text_color,
  }),
  center_text_disabled_color: Schema.optionalWith(Schema.String, {
    default: () => WHEEL_COLOR_DEFAULTS.center_text_disabled_color,
  }),
  sector_fallback_color: Schema.optionalWith(Schema.String, {
    default: () => WHEEL_COLOR_DEFAULTS.sector_fallback_color,
  }),
  // null = 按扇区底色明暗自动取黑/白；非 null = 固定文字色。
  sector_text_color: Schema.optionalWith(Schema.NullOr(Schema.String), { default: () => null }),
  sound_enabled: Schema.Boolean,
  sound_spin_url: Schema.NullOr(Schema.String),
  sound_win_url: Schema.NullOr(Schema.String),
});
export type WheelConfig = Schema.Schema.Type<typeof WheelConfig>;

export const GameUser = Schema.Struct({
  id: Schema.UUID,
  tgUserId: Schema.Number,
  tgUsername: Schema.NullOr(Schema.String),
  tgFirstName: Schema.NullOr(Schema.String),
  spins: Schema.Number,
  totalSpins: Schema.Number,
  preferredLanguage: Schema.NullOr(Schema.String),
  lastActiveAt: Schema.NullOr(Schema.String),
  // 已绑定 WG 时为 platform_user_idx，未绑定 null。Mini App EarnSheet 据此为任务跳转
  // 拼 ?id=<idx>，跟 Invite 页邀请链接同源（差别只在 id），避免用户看到两个不同链接困惑。
  platformUserIdx: Schema.NullOr(Schema.Number),
});
export type GameUser = Schema.Schema.Type<typeof GameUser>;

export const BotPublicInfo = Schema.Struct({
  code: Schema.String,
  name: Schema.String,
  currency: Schema.String,
  defaultLanguage: Schema.String,
  timezone: Schema.String,
  miniAppUrl: Schema.String,
  // WG 平台站点（5dvip.vip 等）。Mini App EarnSheet 的「充值 / 签到」点击跳此站点；
  // 未配置（null）时保持说明性不可点。
  inviteSiteUrl: Schema.NullOr(Schema.String),
  wheelConfig: WheelConfig,
  // 奖励规则（前端 EarnSheet 据此动态渲染各任务券数）。
  rewardRules: RewardRules,
  configVersion: Schema.Number,
});
export type BotPublicInfo = Schema.Schema.Type<typeof BotPublicInfo>;

export const MeResponse = Schema.Struct({
  user: GameUser,
  bot: BotPublicInfo,
  texts: Schema.Record({ key: Schema.String, value: Schema.String }),
  languages: Schema.Array(Schema.String),
  // 今日是否已首充签到（由 spin_grant_log daily_checkin 当日记录判定）。
  checkinToday: Schema.Boolean,
});
export type MeResponse = Schema.Schema.Type<typeof MeResponse>;

export const PrizeListItem = Schema.Struct({
  id: Schema.UUID,
  code: Schema.String,
  name: Schema.String,
  // 转盘扇区副标题（小字，按语言解析；无则 null）
  subtitle: Schema.NullOr(Schema.String),
  // 转盘扇区标题展示方式：text -> 名称文字；image -> imageUrl 图片
  titleMode: Schema.Literal("text", "image", "image_text"),
  type: PrizeType,
  valueAmount: Schema.String,
  valueCurrency: Schema.NullOr(Schema.String),
  requiresShipping: Schema.Boolean,
  displayOrder: Schema.Number,
  displayColor: Schema.NullOr(Schema.String),
  displayIcon: Schema.NullOr(Schema.String),
  imageUrl: Schema.NullOr(Schema.String),
  isJackpot: Schema.Boolean,
});
export type PrizeListItem = Schema.Schema.Type<typeof PrizeListItem>;

export const PrizeListResponse = Schema.Struct({
  configVersion: Schema.Number,
  prizes: Schema.Array(PrizeListItem),
});
export type PrizeListResponse = Schema.Schema.Type<typeof PrizeListResponse>;

export const SpinPrize = Schema.Struct({
  id: Schema.UUID,
  code: Schema.String,
  name: Schema.String,
  type: PrizeType,
  valueAmount: Schema.String,
  valueCurrency: Schema.NullOr(Schema.String),
  isJackpot: Schema.Boolean,
  requiresShipping: Schema.Boolean,
  claimInstructions: Schema.NullOr(Schema.String),
  displayColor: Schema.NullOr(Schema.String),
  displayIcon: Schema.NullOr(Schema.String),
});
export type SpinPrize = Schema.Schema.Type<typeof SpinPrize>;

export const SpinRequest = Schema.Struct({ clientRequestId: Schema.UUID });
export type SpinRequest = Schema.Schema.Type<typeof SpinRequest>;

export const SpinResponse = Schema.Struct({
  spinId: Schema.UUID,
  prize: SpinPrize,
  targetIndex: Schema.Number,
  remainingSpins: Schema.Number,
});
export type SpinResponse = Schema.Schema.Type<typeof SpinResponse>;

// wg.pay 现金奖的派彩状态：claimable=可领取(未派彩) / processing=派彩中 /
// paid=已到账 / failed=失败。非 wg.pay 奖为 null。
export const PayoutStatus = Schema.Literal("claimable", "processing", "paid", "failed");
export type PayoutStatus = Schema.Schema.Type<typeof PayoutStatus>;

export const HistoryItem = Schema.Struct({
  id: Schema.UUID,
  prize: Schema.Struct({
    id: Schema.UUID,
    name: Schema.String,
    type: PrizeType,
    valueAmount: Schema.String,
    valueCurrency: Schema.NullOr(Schema.String),
    requiresShipping: Schema.Boolean,
    claimInstructions: Schema.NullOr(Schema.String),
  }),
  isJackpot: Schema.Boolean,
  needsShippingInput: Schema.Boolean,
  payoutStatus: Schema.NullOr(PayoutStatus),
  createdAt: Schema.String,
});

export const HistoryResponse = Schema.Struct({
  items: Schema.Array(HistoryItem),
  nextCursor: Schema.NullOr(Schema.String),
});
export type HistoryResponse = Schema.Schema.Type<typeof HistoryResponse>;

export const ShippingAddress = Schema.Struct({
  recipientName: Schema.String.pipe(Schema.minLength(1), Schema.maxLength(200)),
  phone: Schema.String.pipe(Schema.pattern(/^\d{10}$/)),
  country: Schema.String.pipe(Schema.minLength(2), Schema.maxLength(8)),
  state: Schema.String.pipe(Schema.minLength(1), Schema.maxLength(200)),
  city: Schema.String.pipe(Schema.minLength(1), Schema.maxLength(200)),
  postalCode: Schema.String.pipe(Schema.pattern(/^[1-8]\d{5}$/)),
  addressLine1: Schema.String.pipe(Schema.minLength(1), Schema.maxLength(200)),
  addressLine2: Schema.NullOr(Schema.String.pipe(Schema.maxLength(200))),
  note: Schema.NullOr(Schema.String.pipe(Schema.maxLength(500))),
});
export type ShippingAddress = Schema.Schema.Type<typeof ShippingAddress>;

export const ShippingAddressResponse = Schema.extend(
  ShippingAddress,
  Schema.Struct({ updatedAt: Schema.String }),
);
export type ShippingAddressResponse = Schema.Schema.Type<typeof ShippingAddressResponse>;

export const ConfirmShippingResponse = Schema.Struct({
  ok: Schema.Literal(true),
  confirmedAt: Schema.String,
});

export const LanguagePatchRequest = Schema.Struct({
  preferredLanguage: Schema.NullOr(Schema.String),
});

// ============================================================
// Admin OAuth Gateway
// ============================================================

export const AdminRoleLiteral = Schema.Literal("owner", "operator");
export type AdminRoleLiteral = Schema.Schema.Type<typeof AdminRoleLiteral>;

export const OAuthCallbackRequest = Schema.Struct({
  code: Schema.String.pipe(Schema.minLength(1)),
  codeVerifier: Schema.String.pipe(Schema.minLength(1)),
  state: Schema.String,
});
export type OAuthCallbackRequest = Schema.Schema.Type<typeof OAuthCallbackRequest>;

export const OAuthCallbackResponse = Schema.Struct({
  ok: Schema.Literal(true),
  expiresIn: Schema.Number,
});
export type OAuthCallbackResponse = Schema.Schema.Type<typeof OAuthCallbackResponse>;

export const AdminMeResponse = Schema.Struct({
  tgUserId: Schema.Number,
  telegramUsername: Schema.NullOr(Schema.String),
  name: Schema.String,
  avatarUrl: Schema.NullOr(Schema.String),
  groups: Schema.Array(Schema.String),
  role: AdminRoleLiteral,
});
export type AdminMeResponse = Schema.Schema.Type<typeof AdminMeResponse>;

export const AdminLogoutResponse = Schema.Struct({
  ok: Schema.Literal(true),
  redirectUrl: Schema.String,
});

// ============================================================
// Managed Bots Onboarding
// ============================================================

export const CreateOnboardSessionRequest = Schema.Struct({
  suggestedUsername: Schema.optionalWith(
    Schema.String.pipe(Schema.pattern(/^[A-Za-z][A-Za-z0-9_]{4,31}$/)),
    { nullable: true },
  ),
  suggestedName: Schema.optionalWith(Schema.String.pipe(Schema.maxLength(64)), {
    nullable: true,
  }),
});
export type CreateOnboardSessionRequest = Schema.Schema.Type<typeof CreateOnboardSessionRequest>;

export const CreateOnboardSessionResponse = Schema.Struct({
  onboardToken: Schema.String,
  deepLink: Schema.String,
  expiresAt: Schema.String,
});
export type CreateOnboardSessionResponse = Schema.Schema.Type<typeof CreateOnboardSessionResponse>;

export const OnboardSessionStatus = Schema.Literal("pending", "completed", "expired", "cancelled");
export type OnboardSessionStatus = Schema.Schema.Type<typeof OnboardSessionStatus>;

export const OnboardSessionStatusResponse = Schema.Struct({
  status: OnboardSessionStatus,
  botInstanceId: Schema.NullOr(Schema.UUID),
  expiresAt: Schema.String,
});
export type OnboardSessionStatusResponse = Schema.Schema.Type<typeof OnboardSessionStatusResponse>;

// ============================================================
// Bot readiness
// ============================================================

export const ReadinessResponse = Schema.Struct({
  ready: Schema.Boolean,
  missing: Schema.Array(Schema.String),
  identity: Schema.NullOr(
    Schema.Struct({
      id: Schema.Number,
      username: Schema.String,
    }),
  ),
});
export type ReadinessResponse = Schema.Schema.Type<typeof ReadinessResponse>;

export const BotSummary = Schema.Struct({
  id: Schema.UUID,
  code: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  botUsername: Schema.NullOr(Schema.String),
  botTelegramId: Schema.NullOr(Schema.Number),
  isEnabled: Schema.Boolean,
  mode: Schema.Literal("polling", "webhook"),
  botType: Schema.Literal("lottery", "analytics"),
  defaultLanguage: Schema.String,
  // 启用语言集（en-IN 强制常含）。
  languages: Schema.Array(Schema.String),
  timezone: Schema.String,
  currency: Schema.String,
  miniAppUrl: Schema.String,
  miniAppShortName: Schema.NullOr(Schema.String),
  // WG 玩家产品站域名，/invite 与 Mini App 邀请页据此构造拉新注册链接
  inviteSiteUrl: Schema.NullOr(Schema.String),
  dauEstimate: Schema.Number,
  wheelConfig: WheelConfig,
  features: Schema.Unknown,
  branding: Schema.Unknown,
  links: Schema.Unknown,
  rewardRules: RewardRules,
  configVersion: Schema.Number,
  lastRuntimeError: Schema.NullOr(Schema.String),
  // Payment 集成 (Phase 1.5)：GET /bots/:id 需回填这些字段，否则 Admin PaymentTab
  // 表单初始化拿不到现值，保存时会把配置清空。
  paymentInstanceId: Schema.NullOr(Schema.UUID),
  paymentChildSiteCode: Schema.NullOr(Schema.String),
  activityStartAt: Schema.NullOr(Schema.String),
  activityEndAt: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  updatedAt: Schema.String,
});
export type BotSummary = Schema.Schema.Type<typeof BotSummary>;

export const CreateBotRequest = Schema.Struct({
  code: Schema.String.pipe(Schema.pattern(/^[a-z0-9-]+$/)),
  name: Schema.String,
  description: Schema.optionalWith(Schema.String, { nullable: true }),
  telegramBotToken: Schema.String,
  currency: Schema.String,
  miniAppUrl: Schema.String,
  miniAppShortName: Schema.optionalWith(Schema.String, { nullable: true }),
  defaultLanguage: Schema.optionalWith(Schema.String, { nullable: true }),
  timezone: Schema.optionalWith(Schema.String, { nullable: true }),
  dauEstimate: Schema.optionalWith(Schema.Number, { nullable: true }),
  botType: Schema.optionalWith(Schema.Literal("lottery", "analytics"), { nullable: true }),
});

export const PatchBotRequest = Schema.Struct({
  name: Schema.optionalWith(Schema.String, { nullable: true }),
  description: Schema.optionalWith(Schema.String, { nullable: true }),
  currency: Schema.optionalWith(Schema.String, { nullable: true }),
  miniAppUrl: Schema.optionalWith(Schema.String, { nullable: true }),
  miniAppShortName: Schema.optionalWith(Schema.String, { nullable: true }),
  inviteSiteUrl: Schema.optionalWith(Schema.String, { nullable: true }),
  rewardRules: Schema.optionalWith(RewardRules, { nullable: true }),
  defaultLanguage: Schema.optionalWith(Schema.String, { nullable: true }),
  languages: Schema.optionalWith(Schema.Array(Schema.String), { nullable: true }),
  timezone: Schema.optionalWith(Schema.String, { nullable: true }),
  dauEstimate: Schema.optionalWith(Schema.Number, { nullable: true }),
  isEnabled: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  botType: Schema.optionalWith(Schema.Literal("lottery", "analytics"), { nullable: true }),
  wheelConfig: Schema.optionalWith(WheelConfig, { nullable: true }),
  // bot.links：客服 / 外链配置（如 support_handle 供 /support 命令文案占位）
  links: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.String }), {
    nullable: true,
  }),
  // Payment 集成 (Phase 1.5)
  paymentInstanceId: Schema.optionalWith(Schema.UUID, { nullable: true }),
  activityStartAt: Schema.optionalWith(Schema.DateFromString, { nullable: true }),
  activityEndAt: Schema.optionalWith(Schema.DateFromString, { nullable: true }),
});

export const CreatePrizeRequest = Schema.Struct({
  code: Schema.String,
  name: Schema.String,
  description: Schema.optionalWith(Schema.String, { nullable: true }),
  type: PrizeType,
  valueAmount: Schema.optionalWith(Schema.Number, { nullable: true }),
  valueCurrency: Schema.optionalWith(Schema.String, { nullable: true }),
  weight: Schema.Number.pipe(Schema.greaterThanOrEqualTo(0)),
  stock: Schema.optionalWith(Schema.Number, { nullable: true }),
  dailyLimit: Schema.optionalWith(Schema.Number, { nullable: true }),
  isJackpot: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  requiresShipping: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  prizeMetadata: Schema.optionalWith(Schema.Unknown, { nullable: true }),
  displayOrder: Schema.optionalWith(Schema.Number, { nullable: true }),
  displayColor: Schema.optionalWith(Schema.String, { nullable: true }),
  displayIcon: Schema.optionalWith(Schema.String, { nullable: true }),
  imageUrl: Schema.optionalWith(Schema.String, { nullable: true }),
  titleMode: Schema.optionalWith(Schema.Literal("text", "image", "image_text"), { nullable: true }),
  i18nName: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.String }), {
    nullable: true,
  }),
  i18nSubtitle: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.String }), {
    nullable: true,
  }),
  // 上架时间窗口：PrizeRepo.listActive 已按 starts_at/ends_at 过滤，此处补齐入口。
  startsAt: Schema.optionalWith(Schema.DateFromString, { nullable: true }),
  endsAt: Schema.optionalWith(Schema.DateFromString, { nullable: true }),
});

export const GrantSpinsRequest = Schema.Struct({
  // 上下界防运营手滑（多打几个 0）；负数允许用于扣减，但同样有界。
  delta: Schema.Number.pipe(Schema.int(), Schema.between(-1000, 1000)),
  reason: Schema.String.pipe(Schema.minLength(1)),
});

// Admin 邀请申报列表：展示 invite_claim + 邀请人 tg 信息 + 最近一次检查原因。
export const AdminInviteClaimItem = Schema.Struct({
  id: Schema.UUID,
  status: Schema.Literal("pending_deposit", "granted"),
  inviterUserId: Schema.UUID,
  inviterTgUserId: Schema.Number,
  inviterTgUsername: Schema.NullOr(Schema.String),
  inviteePlatformUserIdx: Schema.Number,
  inviteeAccount: Schema.String,
  inviteeSiteCode: Schema.String,
  spinsGranted: Schema.NullOr(Schema.Number),
  lastCheckReason: Schema.NullOr(Schema.String),
  lastCheckedAt: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  grantedAt: Schema.NullOr(Schema.String),
});
export type AdminInviteClaimItem = Schema.Schema.Type<typeof AdminInviteClaimItem>;

export const AdminInviteClaimList = Schema.Struct({
  items: Schema.Array(AdminInviteClaimItem),
});
export type AdminInviteClaimList = Schema.Schema.Type<typeof AdminInviteClaimList>;

export const SimulateRequest = Schema.Struct({
  iterations: Schema.Number.pipe(Schema.between(100, 1_000_000)),
});

export const SimulateResponse = Schema.Struct({
  iterations: Schema.Number,
  results: Schema.Array(
    Schema.Struct({
      prizeId: Schema.UUID,
      code: Schema.String,
      expected: Schema.Number,
      actual: Schema.Number,
      deviation: Schema.Number,
    }),
  ),
});

export const PatchPrizeRequest = Schema.Struct({
  type: Schema.optionalWith(PrizeType, { nullable: true }),
  name: Schema.optionalWith(Schema.String, { nullable: true }),
  description: Schema.optionalWith(Schema.String, { nullable: true }),
  valueAmount: Schema.optionalWith(Schema.Number, { nullable: true }),
  valueCurrency: Schema.optionalWith(Schema.String, { nullable: true }),
  weight: Schema.optionalWith(Schema.Number.pipe(Schema.greaterThanOrEqualTo(0)), {
    nullable: true,
  }),
  stock: Schema.optionalWith(Schema.Number, { nullable: true }),
  dailyLimit: Schema.optionalWith(Schema.Number, { nullable: true }),
  isJackpot: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  isEnabled: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  requiresShipping: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  prizeMetadata: Schema.optionalWith(Schema.Unknown, { nullable: true }),
  displayOrder: Schema.optionalWith(Schema.Number, { nullable: true }),
  displayColor: Schema.optionalWith(Schema.String, { nullable: true }),
  displayIcon: Schema.optionalWith(Schema.String, { nullable: true }),
  imageUrl: Schema.optionalWith(Schema.String, { nullable: true }),
  titleMode: Schema.optionalWith(Schema.Literal("text", "image", "image_text"), { nullable: true }),
  i18nName: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.String }), {
    nullable: true,
  }),
  i18nSubtitle: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.String }), {
    nullable: true,
  }),
  startsAt: Schema.optionalWith(Schema.DateFromString, { nullable: true }),
  endsAt: Schema.optionalWith(Schema.DateFromString, { nullable: true }),
});

export const ChannelPurpose = Schema.Literal("broadcast", "support", "admin_notify", "referral");

export const CreateChannelRequest = Schema.Struct({
  purpose: ChannelPurpose,
  telegramChatId: Schema.Number.pipe(Schema.int()),
});

export const PatchChannelRequest = Schema.Struct({
  isEnabled: Schema.optionalWith(Schema.Boolean, { nullable: true }),
  displayOrder: Schema.optionalWith(Schema.Number, { nullable: true }),
});

export const CreateCommandRequest = Schema.Struct({
  command: Schema.String.pipe(Schema.pattern(/^[a-z0-9_]+$/)),
  languageCode: Schema.String,
  label: Schema.String.pipe(Schema.minLength(1)),
  replyTemplate: Schema.optionalWith(Schema.String, { nullable: true }),
  displayOrder: Schema.optionalWith(Schema.Number, { nullable: true }),
});

export const PatchCommandRequest = Schema.Struct({
  label: Schema.optionalWith(Schema.String, { nullable: true }),
  replyTemplate: Schema.optionalWith(Schema.String, { nullable: true }),
  displayOrder: Schema.optionalWith(Schema.Number, { nullable: true }),
  isEnabled: Schema.optionalWith(Schema.Boolean, { nullable: true }),
});

export const CreateTextRequest = Schema.Struct({
  key: Schema.String.pipe(Schema.minLength(1)),
  languageCode: Schema.String,
  text: Schema.String,
});

export const PatchTextRequest = Schema.Struct({
  text: Schema.String,
});

export const PreviewTextRequest = Schema.Struct({
  key: Schema.String,
  vars: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.String }), {
    nullable: true,
  }),
  languageCode: Schema.String,
});

export const BanUserRequest = Schema.Struct({
  banReason: Schema.NullOr(Schema.String),
});

// ============================================================
// Import Managed Bot (Admin direct import without onboard session)
// ============================================================

export const ImportManagedBotRequest = Schema.Struct({
  newBotTelegramId: Schema.Number.pipe(Schema.int()),
  suggestedUsername: Schema.optionalWith(
    Schema.String.pipe(Schema.pattern(/^[A-Za-z][A-Za-z0-9_]{4,31}$/)),
    { nullable: true },
  ),
  suggestedName: Schema.optionalWith(Schema.String.pipe(Schema.maxLength(64)), { nullable: true }),
});
export type ImportManagedBotRequest = Schema.Schema.Type<typeof ImportManagedBotRequest>;
